/**
 * ASK to Draft Export
 * Handles exporting ASK mode responses to Draft mode.
 * Ported from front-react/src/lib/askToDraft.ts
 *
 * Uses the existing createDraft from firestore/draft.ts
 * (same Firestore collection "sensei-drafts" as the React app).
 */

import { createDraft } from "@/platform/firebase/firestore/draft";
import {
  parseMessageIntoSections,
  extractTitle,
  convertSectionToHtml,
} from "./askToDraftParser";

export interface ExportToDraftOptions {
  messageContent: string;
  conversationTitle?: string;
  userQuery?: string;
  messageId?: string;
  sessionId?: string;
  mode?: "doc" | "deck";
}

export interface ExportToDraftResult {
  draftId: string;
  sectionsCount: number;
}

/**
 * Export an ASK mode response to a new Draft document.
 * Parses markdown → structured sections → HTML → Firestore.
 */
export async function exportAskResponseToDraft(
  userId: string,
  options: ExportToDraftOptions
): Promise<ExportToDraftResult> {
  const {
    messageContent,
    conversationTitle,
    userQuery,
    messageId,
    sessionId,
    mode = "doc",
  } = options;

  // Parse message content into sections
  const sections = parseMessageIntoSections(messageContent);

  // Convert markdown to HTML for each section
  const htmlSections = sections.map(convertSectionToHtml);

  // Generate title (use conversation title or extract from content)
  const title =
    conversationTitle ||
    extractTitle(messageContent, "Imported from ASK");

  // Create draft response structure
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const source: Record<string, any> = {
    type: "ask_mode",
    imported_at: new Date().toISOString(),
  };

  if (messageId) source.message_id = messageId;
  if (sessionId) source.session_id = sessionId;
  if (userQuery) source.original_query = userQuery;

  const response = {
    title,
    description: userQuery || "Imported from ASK mode",
    sections: htmlSections.map((section) => ({
      title: section.title,
      content: section.content,
      ...(section.rawContent ? { rawContent: section.rawContent } : {}),
      level: section.level,
    })),
    source,
  };

  // Create draft in Firestore
  const draft = await createDraft(
    userId,
    title,
    userQuery || "Imported from ASK mode",
    response,
    mode
  );

  return {
    draftId: draft.id,
    sectionsCount: htmlSections.length,
  };
}
